import React, { useCallback, useEffect, useState } from "react"
import {
	Grid,
	TextField,
	Select,
	MenuItem,
	FormControl,
	Avatar,
	Chip,
	IconButton,
	InputAdornment,
	Typography,
	Box
} from "@material-ui/core"
import {
	Add as AddIcon,
	Edit as EditIcon,
	Delete as DeleteIcon,
	Search as SearchIcon,
	KeyboardArrowDown as KeyboardArrowDownIcon
} from "@material-ui/icons"
import { useHistory } from "react-router-dom"
import { Button } from "letalkui"
import { Skeleton } from "@material-ui/lab"

import { Notification, Portlet, PopConfirm, UpsellDialog } from "@/components"
import Breadcrumb from "@/components/BreadcrumbNew"
import DataTableWithPagination from "@/components/DataTableWithPagination"
import { TableColumn } from "@/components/DataTable"

import useDidMount from "@/hooks/useDidMount"
import useSubscriptionLimits from "@/hooks/useSubscriptionLimits"
import useValidation, { ErrorType } from "@/hooks/useValidation"

import ErrorHandlerService from "@/services/ErrorHandler"
import MeetingAnalyzerService, {
	MeetingAnalysis,
	MeetingProfile,
	MeetingScoreLabel
} from "@/services/MeetingAnalyzerService"
import UserInInstanceService from "@/services/UserInInstance"
import { UserInInstance } from "@/protocols/userInInstance"

import { DEFAULT_WHERE_DATA, DefaultWhereDataType } from "@/utils/pagination"
import { formatDateInBrazilianDate, formatDateToYYYYMMDD, getDateWithBrazilianTimezone } from "@/utils/time"

import useStyles from "@/pages/Admin/MeetingAnalyzer/styles"
import CreateDialog from "@/pages/Admin/MeetingAnalyzer/CreateDialog"
import DateEditPopover, { DateEditState } from "@/pages/Admin/MeetingAnalyzer/Detail/DateEditPopover"
import {
	getScoreLabel,
	objectionToDisplayText,
	profileToDisplayText,
	scoreLabelToColor
} from "@/utils/meetingAnalyzer"

const PERIOD_OPTIONS = [
	{ value: "", label: "Todo o período" },
	{ value: "7", label: "7 dias" },
	{ value: "30", label: "30 dias" },
	{ value: "90", label: "90 dias" }
]

const PROFILE_OPTIONS: { value: string; label: string }[] = [
	{ value: "", label: "Todos os perfis" },
	{ value: "aggressive", label: "Agressivo" },
	{ value: "balanced", label: "Equilibrado" },
	{ value: "passive", label: "Passivo" },
	{ value: "technical", label: "Técnico" }
]

const SCORE_LABEL_OPTIONS: { value: string; label: string }[] = [
	{ value: "", label: "Todos os scores" },
	{ value: "Alto", label: "Alto" },
	{ value: "Medio", label: "Médio" },
	{ value: "Baixo", label: "Baixo" }
]

const NO_DATE_LABEL = "não identificada"
const ANALYZED_AT_LABEL = "Analisada em"
const NO_LEAD_LABEL = "—"
const SKELETON_ROW_COUNT = 6

type MeetingAnalysisFilters = {
	search: string
	userId: string
	profile: string
	period: string
	scoreLabel: string
}

const INITIAL_FILTERS: MeetingAnalysisFilters = {
	search: "",
	userId: "",
	profile: "",
	period: "90",
	scoreLabel: ""
}

type MeetingAnalysisTableRow = {
	meetingDate: MeetingAnalysis
	closer: MeetingAnalysis
	lead: MeetingAnalysis
	score: MeetingAnalysis
	profile: MeetingAnalysis
	objection: MeetingAnalysis
	conversion: MeetingAnalysis
	action: MeetingAnalysis
}

type DateEditState = {
	analysisId: number
	meetingDate: Date | null
	anchorElement: HTMLButtonElement | null
}

const getInitials = (name: string): string => {
	const words = name.trim().split(/\s+/)

	if (words.length === 0) {
		return "?"
	}

	if (words.length === 1) {
		return words[0].charAt(0).toUpperCase()
	}

	const firstInitial = words[0].charAt(0).toUpperCase()
	const lastInitial = words[words.length - 1].charAt(0).toUpperCase()

	return `${firstInitial}${lastInitial}`
}

const formatMeetingDate = (meetingDate: string | null): string => {
	if (meetingDate === null) {
		return NO_DATE_LABEL
	}

	return formatDateInBrazilianDate(getDateWithBrazilianTimezone(meetingDate))
}

const MeetingAnalyzerList: React.FC = () => {
	const classes = useStyles()
	const history = useHistory()
	const { triggerValidation } = useValidation()

	const meetingAnalyzerPermission = useSubscriptionLimits("permission", "meeting_analyzer")
	const isMeetingAnalyzerDisabled = meetingAnalyzerPermission.isLoading

	const [analyses, setAnalyses] = useState<MeetingAnalysis[]>([])
	const [totalCount, setTotalCount] = useState(0)
	const [loading, setLoading] = useState(true)
	const [closers, setClosers] = useState<UserInInstance[]>([])
	const [filters, setFilters] = useState<MeetingAnalysisFilters>(INITIAL_FILTERS)
	const [paginationState, setPaginationState] = useState<DefaultWhereDataType>(DEFAULT_WHERE_DATA)
	const [dateEdit, setDateEdit] = useState<DateEditState | null>(null)

	const formatRow = (analysis: MeetingAnalysis): MeetingAnalysisTableRow => {
		return {
			meetingDate: analysis,
			closer: analysis,
			lead: analysis,
			score: analysis,
			profile: analysis,
			objection: analysis,
			conversion: analysis,
			action: analysis
		}
	}

	const handleGetAnalyses = useCallback(async () => {
		setLoading(true)

		try {
			const userId = filters.userId ? Number(filters.userId) : undefined
			const profile = filters.profile ? (filters.profile as MeetingProfile) : undefined
			const scoreLabel = filters.scoreLabel ? (filters.scoreLabel as MeetingScoreLabel) : undefined

			const response = await MeetingAnalyzerService.retrieveAll({
				search: filters.search || undefined,
				userId,
				profile,
				period: filters.period || undefined,
				scoreLabel,
				page: paginationState.page,
				limit: paginationState.rowsPerPage
			})

			setAnalyses(response.analyses)
			setTotalCount(response.count)
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}

		setLoading(false)
	}, [filters, paginationState])

	const handleLoadClosers = useCallback(async () => {
		const closersList = await UserInInstanceService.getUsersInInstance({ status: ["active"] })

		if (closersList) {
			setClosers(closersList)
		}
	}, [])

	useDidMount(() => {
		handleLoadClosers()
	})

	useEffect(() => {
		handleGetAnalyses()
	}, [handleGetAnalyses])

	const handleApplyFilters = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()

		setPaginationState({ ...paginationState, page: 0 })
	}

	const handlePaginationChange = (data: Partial<DefaultWhereDataType>) => {
		setPaginationState(lastState => ({
			...lastState,
			...data
		}))
	}

	const handleFilterChange = (key: keyof MeetingAnalysisFilters, value: string) => {
		setFilters(lastFilters => ({
			...lastFilters,
			[key]: value
		}))
	}

	const handleRowClick = (analysis: MeetingAnalysis) => {
		history.push(`/admin/meeting-analyzer/${analysis.id}`)
	}

	const handleOpenDateEdit = (analysis: MeetingAnalysis, anchorElement: HTMLButtonElement) => {
		if (!analysis.output) {
			return
		}

		const initialDate = analysis.output.meetingDate ? new Date(`${analysis.output.meetingDate}T00:00:00`) : null

		setDateEdit({
			analysisId: analysis.id,
			meetingDate: initialDate,
			anchorElement
		})
	}

	const handleCloseDateEdit = () => {
		setDateEdit(null)
	}

	const handleConfirmDateEdit = async (selectedDate: Date | null) => {
		if (!dateEdit || !selectedDate) {
			handleCloseDateEdit()
			return
		}

		const meetingDate = formatDateToYYYYMMDD(selectedDate)

		try {
			await MeetingAnalyzerService.updateDate(dateEdit.analysisId, { meetingDate })

			setAnalyses(prev => prev.map(analysis => analysis.id === dateEdit.analysisId ? { ...analysis, output: analysis.output ? { ...analysis.output, meetingDate } : analysis.output } : analysis))

			Notification.success({ message: "Data da reunião atualizada com sucesso." })
		} catch (error) {
			triggerValidation(error as ErrorType)
		}

		handleCloseDateEdit()
	}

	const handleDeleteAnalysis = (analysis: MeetingAnalysis) => {
		PopConfirm.open({
			title: "Excluir análise",
			description: "Deseja realmente excluir esta análise? Esta ação não pode ser desfeita.",
			confirmButtonText: "EXCLUIR",
			onConfirm: async () => {
				try {
					await MeetingAnalyzerService.delete(analysis.id)

					setAnalyses(prev => prev.filter(item => item.id !== analysis.id))
					setTotalCount(prev => Math.max(prev - 1, 0))

					Notification.success({ message: "Análise excluída com sucesso." })
				} catch (error) {
					ErrorHandlerService.handle(error as ErrorType)
				}
			}
		})
	}

	const handleCreateSuccess = (analysisId: number) => {
		history.push(`/admin/meeting-analyzer/${analysisId}`)
	}

	const handleAnalyzeMeetingClick = () => {
		if (meetingAnalyzerPermission.mustBlock) {
			UpsellDialog.open({ dialogCode: meetingAnalyzerPermission.blockCode })
			return
		}

		CreateDialog.open({ onSuccess: handleCreateSuccess })
	}

	const renderMeetingDateCell = (analysis: MeetingAnalysis) => {
		const hasOutput = analysis.output !== null

		return (
			<Grid container direction="column" className={classes.dateCell}>
				<Grid item>
					<Grid container alignItems="center" spacing={1} className={classes.datePrimaryRow}>
						<Grid item>
							<Typography className={classes.datePrimary}>
								{hasOutput ? formatMeetingDate(analysis.output.meetingDate) : NO_LEAD_LABEL}
							</Typography>
						</Grid>
						{hasOutput && (
							<Grid item>
								<IconButton
									className={classes.editIconButton}
									size="small"
									onClick={(event) => {
										event.stopPropagation()
										handleOpenDateEdit(analysis, event.currentTarget)
									}}
								>
									<EditIcon fontSize="small" />
								</IconButton>
							</Grid>
						)}
					</Grid>
				</Grid>
				<Grid item>
					<Typography className={classes.dateSecondary}>
						{ANALYZED_AT_LABEL} {formatDateInBrazilianDate(new Date(analysis.createdAt))}
					</Typography>
				</Grid>
			</Grid>
		)
	}

	const renderCloserCell = (analysis: MeetingAnalysis) => {
		return (
			<Box className={classes.closerCell}>
				<Avatar className={classes.closerAvatar}>
					{getInitials(analysis.userName)}
				</Avatar>
				<Typography className={classes.userName}>
					{analysis.userName}
				</Typography>
			</Box>
		)
	}

	const renderLeadCell = (analysis: MeetingAnalysis) => {
		const leadName = analysis.output?.participants?.leadName
		const leadCompany = analysis.output?.participants?.leadCompany

		return (
			<Grid container direction="column">
				<Grid item>
					<Typography className={classes.leadName}>
						{leadName ?? NO_LEAD_LABEL}
					</Typography>
				</Grid>
				<Grid item>
					<Typography className={classes.leadCompany}>
						{leadCompany ?? NO_LEAD_LABEL}
					</Typography>
				</Grid>
			</Grid>
		)
	}

	const renderScoreCell = (analysis: MeetingAnalysis) => {
		if (!analysis.output) {
			return <Typography>{NO_LEAD_LABEL}</Typography>
		}

		const scoreLabel = getScoreLabel(analysis.output.generalScore)
		const scoreColor = scoreLabelToColor[scoreLabel]

		return (
			<Box className={classes.scoreCell} style={{ backgroundColor: scoreColor.badgeBackground }}>
				<Box className={classes.scoreDot} style={{ backgroundColor: scoreColor.dotColor }} />
				<Typography className={classes.scoreText} style={{ color: scoreColor.textColor }}>
					{analysis.output.generalScore}
				</Typography>
			</Box>
		)
	}

	const renderProfileCell = (analysis: MeetingAnalysis) => {
		if (!analysis.output) {
			return <Typography>{NO_LEAD_LABEL}</Typography>
		}

		return (
			<Chip
				className={classes.profileBadge}
				label={profileToDisplayText[analysis.output.closerProfile.type]}
				size="small"
			/>
		)
	}

	const renderObjectionCell = (analysis: MeetingAnalysis) => {
		if (!analysis.output) {
			return <Typography>{NO_LEAD_LABEL}</Typography>
		}

		return (
			<Chip
				className={classes.objectionBadge}
				label={objectionToDisplayText[analysis.output.mainObjection.category]}
				size="small"
			/>
		)
	}

	const renderConversionCell = (analysis: MeetingAnalysis) => {
		if (!analysis.output) {
			return <Typography>{NO_LEAD_LABEL}</Typography>
		}

		return (
			<Typography className={classes.conversionText}>
				{analysis.output.conversionProbability}%
			</Typography>
		)
	}

	const renderActionCell = (analysis: MeetingAnalysis) => {
		return (
			<IconButton
				onClick={(event) => {
					event.stopPropagation()
					handleDeleteAnalysis(analysis)
				}}
			>
				<DeleteIcon />
			</IconButton>
		)
	}

	const tableColumns: TableColumn<MeetingAnalysisTableRow>[] = [
		{ field: "meetingDate", label: "Data da reunião", rowRender: (value) => renderMeetingDateCell(value as MeetingAnalysis) },
		{ field: "closer", label: "Closer", rowRender: (value) => renderCloserCell(value as MeetingAnalysis) },
		{ field: "lead", label: "Lead", rowRender: (value) => renderLeadCell(value as MeetingAnalysis) },
		{ field: "score", label: "Score", rowRender: (value) => renderScoreCell(value as MeetingAnalysis) },
		{ field: "profile", label: "Perfil", rowRender: (value) => renderProfileCell(value as MeetingAnalysis) },
		{ field: "objection", label: "Objeção", rowRender: (value) => renderObjectionCell(value as MeetingAnalysis) },
		{ field: "conversion", label: "Conversão", headRender: () => <span>Conversão</span>, rowRender: (value) => renderConversionCell(value as MeetingAnalysis) },
		{ field: "action", label: "", rowRender: (value) => renderActionCell(value as MeetingAnalysis) }
	]

	const tableRows = analyses.map(formatRow)

	const showEmptyState = !loading && totalCount === 0

	return (
		<Grid container direction="column" spacing={3}>
			<Grid item>
				<Grid
					container
					direction="column"
					spacing={3}
				>
					<Grid item>
						<Breadcrumb
							data={[
								{ name: "Reuniões analisadas", pathname: "/admin/meeting-analyzer" }
							]}
						/>
					</Grid>
					<Grid item>
						<Grid container direction="column" spacing={4}>
							<Grid item>
								<Grid container justifyContent="space-between" alignItems="center">
									<Grid item>
										<Grid container direction="column" spacing={1}>
											<Grid item>
												<Typography variant="h5" className={classes.titleText}>
													Reuniões analisadas
												</Typography>
											</Grid>
										</Grid>
									</Grid>
									<Grid item>
										<Button.Primary
											startIcon={<AddIcon />}
											onClick={handleAnalyzeMeetingClick}
											size="large"
											disabled={isMeetingAnalyzerDisabled}
										>
											Analisar reunião
										</Button.Primary>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>

			<Grid item xs={12}>
				<Portlet className={classes.listCard}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<form onSubmit={handleApplyFilters}>
								<Grid container spacing={1} xs={12}>
									<Grid item xs={3}>
										<TextField
											fullWidth
											placeholder="Buscar por lead ou empresa..."
											value={filters.search}
											onChange={({ target }) => handleFilterChange("search", target.value)}
											variant="outlined"
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														<SearchIcon fontSize="small" />
													</InputAdornment>
												)
											}}
										/>
									</Grid>
									<Grid item xs={2}>
										<FormControl fullWidth variant="outlined">
											<Select
												value={filters.userId}
												onChange={({ target }) => handleFilterChange("userId", target.value as string)}
												displayEmpty
												IconComponent={KeyboardArrowDownIcon}
											>
												<MenuItem value="">
													Todos os closers
												</MenuItem>
												{closers.map(closer => (
													<MenuItem key={closer.id} value={closer.id}>
														{closer.name}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Grid>
									<Grid item xs={2}>
										<FormControl fullWidth variant="outlined">
											<Select
												value={filters.profile}
												onChange={({ target }) => handleFilterChange("profile", target.value as string)}
												displayEmpty
												IconComponent={KeyboardArrowDownIcon}
											>
												{PROFILE_OPTIONS.map(option => (
													<MenuItem key={option.value} value={option.value}>
														{option.label}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Grid>
									<Grid item xs={2}>
										<FormControl fullWidth variant="outlined">
											<Select
												value={filters.period}
												onChange={({ target }) => handleFilterChange("period", target.value as string)}
												displayEmpty
												IconComponent={KeyboardArrowDownIcon}
											>
												{PERIOD_OPTIONS.map(option => (
													<MenuItem key={option.value} value={option.value}>
														{option.label}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Grid>
									<Grid item xs={2}>
										<FormControl fullWidth variant="outlined">
											<Select
												value={filters.scoreLabel}
												onChange={({ target }) => handleFilterChange("scoreLabel", target.value as string)}
												displayEmpty
												IconComponent={KeyboardArrowDownIcon}
											>
												{SCORE_LABEL_OPTIONS.map(option => (
													<MenuItem key={option.value} value={option.value}>
														{option.label}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Grid>
									<Grid item xs={1}>
										<Button.Primary
											type="submit"
											className={classes.searchButton}
										>
											Buscar
										</Button.Primary>
									</Grid>
								</Grid>
							</form>
						</Grid>

						<Grid item xs={12}>
							{loading ? (
								<Grid container direction="column" spacing={1}>
									{Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIndex) => (
										<Grid item key={`skeleton-${rowIndex}`}>
											<Skeleton variant="rect" height={48} />
										</Grid>
									))}
								</Grid>
							) : showEmptyState ? (
								<Grid container direction="column" alignItems="center" className={classes.emptyState}>
									<Grid item>
										<Typography className={classes.emptyStateTitle}>
											Nenhuma reunião analisada ainda
										</Typography>
									</Grid>
									<Grid item>
										<Typography className={classes.emptyStateSubtitle}>
											Clique em &quot;Analisar reunião&quot; para realizar a sua primeira análise.
										</Typography>
									</Grid>
								</Grid>
							) : (
								<DataTableWithPagination
									columns={tableColumns}
									data={tableRows}
									page={paginationState.page}
									rowsPerPage={paginationState.rowsPerPage}
									totalRows={totalCount}
									onPaginationChange={handlePaginationChange}
									getRowProps={(row) => ({
										className: classes.clickableRow,
										onClick: () => handleRowClick(row.meetingDate)
									})}
								/>
							)}
						</Grid>
					</Grid>
				</Portlet>
			</Grid>

			<DateEditPopover
				value={dateEdit?.meetingDate ?? null}
				anchorElement={dateEdit?.anchorElement ?? null}
				onClose={handleCloseDateEdit}
				onConfirm={handleConfirmDateEdit}
			/>
		</Grid>
	)
}

export default MeetingAnalyzerList
